
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ToastrService } from '../toastr';
// import { environment } from '../../../environments/environment';

declare const environment: any;
declare const environmentResource: any;

export interface INavigatorState {
    show: boolean;
}

// Native dynamic-import() rejection messages for a stale lazy-route chunk (e.g. after a deploy
// replaced content-hashed filenames while this tab still ran the old bundle). Not `ChunkLoadError`
// — this app's esbuild builder never produces that (webpack-only).
const STALE_BUNDLE_ERROR_PATTERNS = [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module'
];

// sessionStorage, not in-memory: the recovery reload this guards is itself a full page reload,
// which would wipe an in-memory guard and loop forever on a genuinely broken route. Not
// LocalStorageService either — that's localStorage-backed and would make the guard permanent.
const RECOVERY_ATTEMPTED_KEY_PREFIX = 'staleBundleRecoveryAttempted:';

@Injectable({
    providedIn: 'root'
})
export class NavigationIndicatorService implements OnDestroy {
    private routerEventSubscription: Subscription;

    private _navigator$ = new Subject<INavigatorState>();
    public navigator$ = this._navigator$.asObservable();

    private routeCache: string[] = [];

    constructor(private router: Router, private toastrService: ToastrService) {

        this.routerEventSubscription = this.router.events
            .pipe(
                tap(event => {
                    if (event instanceof NavigationStart) {
                        const url = this.normalizeUrl(event.url);

                        if (this.routeCache.includes(url)) {
                            return;
                        }
                        return this.show();
                    }

                    if (event instanceof NavigationEnd) {
                        const url = this.normalizeUrl(event.url);

                        this.routeCache.push(url);
                        return this.hide();
                    }

                    if (event instanceof NavigationError) {
                        if (this.isStaleBundleError(event.error) && !this.recoveryAlreadyAttempted(event.url)) {
                            // Stale bundle: recover silently instead of showing the toast below.
                            this.markRecoveryAttempted(event.url);
                            this.reloadToUrl(event.url);
                            return this.hide();
                        }

                        console.log('Navigation error', event);
                        this.toastrService.error(`Error accessing [${event.url}]`);
                        return this.hide();
                    }
                })
            ).subscribe();
    }

    private isStaleBundleError(error: any): boolean {
        const message = String.trimOrEmpty(error?.message).toLowerCase();

        if (String.isNullOrEmpty(message)) {
            return false;
        }

        return STALE_BUNDLE_ERROR_PATTERNS.some(pattern => message.contains(pattern));
    }

    private recoveryAlreadyAttempted(url: string): boolean {
        return sessionStorage.getItem(RECOVERY_ATTEMPTED_KEY_PREFIX + url) != null;
    }

    private markRecoveryAttempted(url: string): void {
        sessionStorage.setItem(RECOVERY_ATTEMPTED_KEY_PREFIX + url, '1');
    }

    // Not `location.reload()` — the address bar hasn't changed to `url` yet (Angular updates it
    // only on NavigationEnd), so that would just reload the current page. Mirrors
    // VersionCheckService's own recovery navigation.
    private reloadToUrl(url: string): void {
        location.href = `${location.origin}${environmentResource.baseUrl}${url}`;
    }

    ngOnDestroy() {
        this.routerEventSubscription.unsubscribe();
    }

    public getNavigator(): Observable<INavigatorState> {
        return this.navigator$;
    }

    public show(): void {
        this._navigator$.next(<INavigatorState>{ show: true });
    }

    public hide(): void {
        this._navigator$.next(<INavigatorState>{ show: false });
    }

    private normalizeUrl(url: string): string {
        url = String.trimOrEmpty(url).toLowerCase();

        if (String.isNullOrEmpty(url)) {
            return url;
        }

        if (url.contains(environment.versionCheckURL)) {
            // We don't want version checking to keep polluting the UI.
            return;
        }

        const qsIndex = url.indexOf('?');

        if (qsIndex >= 0) {
            url = url.substring(0, qsIndex);
        }

        return url;
    }
}
